/**
 * Centralised analytics initialisation for PostHog, Sentry, and GA4.
 *
 * Every provider is gated by an environment variable. When the variable is
 * unset the provider is a no-op, so the app runs identically in tests and
 * local dev without any analytics keys.
 *
 * Call initAnalytics() once at startup (index.js).
 * Call identifyUser(username) after login.
 * Call trackEvent(name, properties) for product events.
 */

import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';

let posthogInitialised = false;
let sentryInitialised = false;

/**
 * Initialise all configured analytics providers.
 * Safe to call in any environment — providers without keys are skipped.
 */
export function initAnalytics() {
  const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
  const posthogHost =
    process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
  const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
  const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  // --- PostHog ---
  if (posthogKey && !posthogInitialised) {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      person_profiles: 'identified_only',
    });
    posthogInitialised = true;
  }

  // --- Sentry ---
  if (sentryDsn && !sentryInitialised) {
    Sentry.init({
      dsn: sentryDsn,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
      ],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });
    sentryInitialised = true;
  }

  // --- Google Analytics 4 ---
  if (gaMeasurementId) {
    if (!window.gtag) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function () {
        window.dataLayer.push(arguments);
      };
      window.gtag('js', new Date());
      window.gtag('config', gaMeasurementId);
    }
  }
}

/**
 * Identify the current user to all configured providers.
 * Call after login, signup, or Google sign-in succeeds.
 *
 * @param {string|null} username - The logged-in user's username, or null on logout.
 */
export function identifyUser(username) {
  const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
  const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
  const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  if (posthogKey && posthogInitialised) {
    if (username) {
      posthog.identify(username);
    } else {
      posthog.reset();
    }
  }

  if (sentryDsn && sentryInitialised) {
    if (username) {
      Sentry.setUser({ username });
    } else {
      Sentry.setUser(null);
    }
  }

  if (gaMeasurementId && window.gtag) {
    window.gtag('event', 'login', {
      method: username ? 'authenticated' : 'logout',
    });
  }
}

/**
 * Capture a product event across all configured providers.
 *
 * @param {string} eventName - The event name (e.g. 'upload_image', 'create_share').
 * @param {object} [properties] - Optional event properties.
 */
export function trackEvent(eventName, properties = {}) {
  const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
  const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;

  if (posthogKey && posthogInitialised) {
    posthog.capture(eventName, properties);
  }

  if (gaMeasurementId && window.gtag) {
    window.gtag('event', eventName, properties);
  }
}

export default { initAnalytics, identifyUser, trackEvent };
